import useCurrentUser from '@/api/auth/useCurrentUser';
import { uploadInlineImage } from '@/lib/email/uploadInlineImage';
import type { UploadImageFn } from './useInsertUploadedImage';

/**
 * Association entity type for images inserted into an email body before the
 * email exists. Must match the backend `EMAIL_INLINE_IMAGE` enum value — the
 * send path authorizes inline images by `(email_inline_image, organizationId)`.
 */
const EMAIL_INLINE_IMAGE_ENTITY_TYPE = 'email_inline_image';

/**
 * Default inline-image uploader for the email editors: stores the image against
 * the current organization so any composer in the org can inline it at send
 * time. Returns `undefined` until the org is known (e.g. before auth resolves),
 * which cleanly disables the image button / paste rather than uploading to an
 * unknown scope.
 */
export function useInlineImageUpload(): UploadImageFn | undefined {
  const { data } = useCurrentUser();
  const organizationId = data?.user?.organizationId;

  if (!organizationId) return undefined;

  // Plain function — React Compiler memoizes it (keyed on organizationId), so a
  // manual useCallback would be redundant.
  const uploader: UploadImageFn = (file) =>
    uploadInlineImage({
      file,
      entityType: EMAIL_INLINE_IMAGE_ENTITY_TYPE,
      entityId: organizationId,
    });

  return uploader;
}
